import { api } from './client'

// ---------- tipuri — oglindesc DTO-urile din Cipral.Application/Admin/AdminModels.cs ----------

export interface RoleRef { id: string; code: string; name: string }
export interface WorkPointRef { id: string; code: string; name: string }
export interface ClientRef { id: string; code: string; name: string }
export interface WarehouseRef { id: string; code: string; name: string }
export interface EquipmentRef { id: string; code: string; name: string }

export interface UserListItem {
  id: string
  userName: string
  email: string
  firstName: string
  lastName: string
  phone: string | null
  department: string | null
  language: string
  isActive: boolean
  mustChangePassword: boolean
  lastLoginAt: string | null
  lockedUntil: string | null
  roles: RoleRef[]
  workPoints: WorkPointRef[]
  clientPortfolio: ClientRef[]
  warehouses: WarehouseRef[]
  equipment: EquipmentRef[]
  delegateToUserId: string | null
  delegateFrom: string | null
  delegateUntil: string | null
}

export interface CreateUserPayload {
  userName: string
  email: string
  firstName: string
  lastName: string
  phone?: string | null
  department?: string | null
  language: string
  roleIds: string[]
  workPointIds: string[]
  clientIds?: string[]
  warehouseIds?: string[]
  equipmentIds?: string[]
}

export interface UpdateUserPayload {
  email: string
  firstName: string
  lastName: string
  phone?: string | null
  department?: string | null
  language: string
  roleIds: string[]
  workPointIds: string[]
  clientIds?: string[]
  warehouseIds?: string[]
  equipmentIds?: string[]
  delegateToUserId?: string | null
  delegateFrom?: string | null
  delegateUntil?: string | null
}

export interface EffectivePermissions {
  userId: string
  roles: string[]
  workPoints: WorkPointRef[]
  permissions: string[]
}

export interface RoleListItem {
  id: string
  code: string
  name: string
  description: string | null
  isSystem: boolean
  isActive: boolean
  permissionCount: number
  userCount: number
}

export interface RoleDetail {
  id: string
  code: string
  name: string
  description: string | null
  isSystem: boolean
  isActive: boolean
  permissionIds: string[]
}

export interface Permission {
  id: string
  code: string
  module: string
  resource: string
  action: string
  name: string
  isFieldLevel: boolean
}
export interface PermissionResourceGroup { resource: string; permissions: Permission[] }
export interface PermissionModuleGroup { module: string; resources: PermissionResourceGroup[] }

export interface RoleImpactPreview {
  affectedUserCount: number
  permissionsGained: string[]
  permissionsLost: string[]
}

export interface WorkPointListItem {
  id: string; code: string; name: string; city: string | null
  isDefault: boolean; isActive: boolean; userCount: number
}

// ---------- utilizatori ----------

export const listUsers = (search?: string) =>
  api.get<UserListItem[]>('/admin/users', { params: search ? { search } : undefined }).then((r) => r.data)

export const createUser = (payload: CreateUserPayload) =>
  api.post<{ id: string; pendingAdminApproval: boolean }>('/admin/users', payload).then((r) => r.data)

export const updateUser = (id: string, payload: UpdateUserPayload) =>
  api.put<{ pendingAdminApproval: boolean }>(`/admin/users/${id}`, payload).then((r) => r.data)

export const deactivateUser = (id: string, reason?: string) =>
  api.post(`/admin/users/${id}/deactivate`, { reason })

export const reactivateUser = (id: string) => api.post(`/admin/users/${id}/reactivate`)
export const resetUserPassword = (id: string) => api.post(`/admin/users/${id}/reset-password`)
export const forcePasswordChange = (id: string) => api.post(`/admin/users/${id}/force-password-change`)
export const unlockUser = (id: string) => api.post(`/admin/users/${id}/unlock`)
export const closeUserSessions = (id: string) => api.post(`/admin/users/${id}/close-sessions`)

export const bulkAssign = (userIds: string[], roleId?: string, workPointId?: string) =>
  api.post<{ updated: number; pendingApprovals: number }>('/admin/users/bulk-assign', { userIds, roleId, workPointId }).then((r) => r.data)

export const getEffectivePermissions = (id: string) =>
  api.get<EffectivePermissions>(`/admin/users/${id}/effective-permissions`).then((r) => r.data)

// ---------- roluri ----------

export const listRoles = () => api.get<RoleListItem[]>('/admin/roles').then((r) => r.data)
export const getRole = (id: string) => api.get<RoleDetail>(`/admin/roles/${id}`).then((r) => r.data)

export const createRole = (payload: { code: string; name: string; description?: string | null; permissionIds: string[] }) =>
  api.post<{ id: string }>('/admin/roles', payload).then((r) => r.data)

export const updateRole = (id: string, payload: { name: string; description?: string | null; permissionIds: string[] }) =>
  api.put(`/admin/roles/${id}`, payload)

export const deactivateRole = (id: string) => api.post(`/admin/roles/${id}/deactivate`)

export const cloneRole = (id: string, newCode: string, newName: string) =>
  api.post<{ id: string }>(`/admin/roles/${id}/clone`, { newCode, newName }).then((r) => r.data)

export const previewRoleImpact = (id: string, permissionIds: string[]) =>
  api.post<RoleImpactPreview>(`/admin/roles/${id}/impact-preview`, { permissionIds }).then((r) => r.data)

// ---------- catalog / suport ----------

export const listPermissions = () => api.get<PermissionModuleGroup[]>('/admin/permissions').then((r) => r.data)
export const listWorkPoints = () => api.get<WorkPointListItem[]>('/admin/work-points').then((r) => r.data)

// ---------- FR-ADM-018 — pickere pentru dimensiunile de restricție de pe formularul de utilizator ----------

export const listAdminClients = () => api.get<ClientRef[]>('/admin/clients').then((r) => r.data)
export const listAdminWarehouses = () => api.get<WarehouseRef[]>('/admin/warehouses').then((r) => r.data)
export const listAdminEquipment = () => api.get<EquipmentRef[]>('/admin/equipment').then((r) => r.data)

// ---------- Audit trail (FR-ADM-011, 012, 014, 031) ----------

export type AuditAction = 'Create' | 'Update' | 'Delete' | 'Deactivate' | 'Login' | 'LoginFailed' | 'Logout' | 'PermissionChange' | 'SecurityIncident'

export interface AuditEntry {
  id: number
  occurredAt: string
  userName: string | null
  action: AuditAction
  entityType: string
  entityId: string | null
  changes: string | null
  reason: string | null
  ipAddress: string | null
}

export interface AuditFilters { entityType?: string; from?: string; to?: string; take?: number }

export const listAudit = (filters?: AuditFilters) => api.get<AuditEntry[]>('/admin/audit', { params: filters }).then((r) => r.data)
export const listAuditEntityTypes = () => api.get<string[]>('/admin/audit/tipuri-entitate').then((r) => r.data)
